import { Button, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { SiTrackPage } from '../../components/si-track-page';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { unitsApiKeyStore } from '../../api/units-api-key-store';
import { columns } from './units-grid-columns';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SelectChainItem, SelectConfig, SelectResult } from '../../components/select/select-chain.component';
import { useEffect, useState } from 'react';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { UnitsCustomGridCell } from './units-custom-grid-cell';
import { UserPermissionName } from '../../models/user-permission-name';
import { GuardedComponent } from '../../components/guarded-component';
import { CreateWorkorderDialog } from '../workorders/create-workorder/create-workorder-dialog';
import { Unit } from '../../models/unit';
import { FormSelect } from '../../components/hook-form/form-select.component';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { useAuthorize } from '../../hooks/use-authorize';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    status: 'active',
  };
};

const options = [
  {
    value: 'all',
    label: 'All',
    isDefault: false,
  },
  {
    value: 'active',
    label: 'Active',
    isDefault: true,
  },
  {
    value: 'archived',
    label: 'Closed',
    isDefault: false,
  },
];

export const UnitsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [enableError, setEnableError] = useState<any>({
    customerError: false,
    officeError: false,
    projectError: false,
  });

  const form = useReactHookForm({
    defaultValues: {
      status: 'active',
    },
  });

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [unitData, setUnitData] = useState<any>();

  const handleAddWorkOrder = (data: any) => {
    setUnitData(data);
    setIsFormOpen(true);
  };

  const handleDialogClose = () => {
    setIsFormOpen(false);
  };

  const selectConfig: SelectConfig[] = [
    {
      title: 'Customer',
      optionsLabelKey: 'customerName',
      optionsValueKey: 'customerID',
      type: 'autocomplete',
      error: `${enableError?.customerError ? 'Select customer' : ''}`,
      getQueryKeyConfig: () => customersApiKeyStore.getCustomers(),
    },
    {
      title: 'Office',
      optionsLabelKey: 'officeName',
      optionsValueKey: 'officeID',
      type: 'autocomplete',
      error: `${enableError?.officeError ? 'Select office' : ''}`,
      getQueryKeyConfig: (value?: SelectResult) => officeApiKeyStore.getCustomerOffices(value?.customerID),
    },
    {
      title: 'Project',
      optionsLabelKey: 'projectName',
      optionsValueKey: 'projectID',
      type: 'autocomplete',
      error: `${enableError?.projectError ? 'Select project' : ''}`,
      getQueryKeyConfig: (value?: SelectResult) => projectsApiKeyStore.getOfficeProjects(value?.officeID),
    },
  ];

  const getAPIKeyStore = () => {
    if (selectedFilters.projectID) {
      // return unitsApiKeyStore.getProjectUnits(selectedFilters?.projectID);
      return unitsApiKeyStore.getProjectUnitsWithStatus(selectedFilters?.projectID, form.getValues('status'));
    }
    if (selectedFilters.officeID) {
      // return unitsApiKeyStore.getOfficeUnits(selectedFilters?.officeID);
      return unitsApiKeyStore.getOfficeUnitsWithStatus(selectedFilters?.officeID, form.getValues('status'));
    }
    if (selectedFilters.customerID) {
      // return unitsApiKeyStore.getCustomerUnits(selectedFilters?.customerID);
      return unitsApiKeyStore.getCustomerUnitsWithStatus(selectedFilters?.customerID, form.getValues('status'));
    }
    if (searchParams.get('searchType') == 'units' && (searchParams.get('searchText') as string))
      return unitsApiKeyStore.getSearchUnits(searchParams.get('searchText'));
    if (searchParams.get('searchType') == 'jobsites' && (searchParams.get('searchText') as string))
      return unitsApiKeyStore.getSearchJobsites(searchParams.get('searchText'));

    return unitsApiKeyStore.getUnitsWithStatus(form.getValues('status'));
  };

  const removeValidation = (filter: SelectResult) => {
    if (filter?.customerID) setEnableError((ps: any) => ({ ...ps, customerError: false }));
    if (filter?.officeID) setEnableError((ps: any) => ({ ...ps, officeError: false }));
    if (filter?.projectID) setEnableError((ps: any) => ({ ...ps, projectError: false }));
  };

  const handleFilterSelected = (filter: SelectResult) => {
    removeValidation(filter);
    setSelectedFilters(filter);

    if (filter) {
      setSearchParams({
        ...(filter.customerID && { customerID: filter.customerID }),
        ...(filter.officeID && { officeID: filter.officeID }),
        ...(filter.projectID && { projectID: filter.projectID }),
      });
    }
  };

  const validateAddLot = () => {
    if (!selectedFilters?.customerID) {
      setEnableError((prevState: any) => ({
        ...prevState,
        customerError: true,
        officeError: true,
        projectError: true,
      }));
      return true;
    }
    if (!selectedFilters?.officeID) {
      setEnableError((prevState: any) => ({
        ...prevState,
        officeError: true,
        projectError: true,
      }));
      return true;
    }
    if (!selectedFilters?.projectID) {
      setEnableError((prevState: any) => ({
        ...prevState,
        projectError: true,
      }));
      return true;
    }
    return false;
  };

  const handleCreateLot = () => {
    // if (validateAddLot()) {
    //   return;
    // }
    // if (selectedFilters?.projectID) navigate(`/projects/edit/${selectedFilters?.projectID}?tab=buildings`);
    navigate('addLotOrUnit', { state: { filters: selectedFilters } });
  };

  const handleEditUnit = (value: Unit) => {
    navigate(`edit/${value.unitID}`);
  };

  const handleStatusChange = (e: any) => {
    setSelectedFilters({ ...selectedFilters, status: e });
  };

  const addWorkOrderElement = (dataItem: any) => {
    const isAddWorkorderPermission = useAuthorize([UserPermissionName.ADD_WORKORDER]);
    return (
      <>
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_WORKORDER]}>
          <IconButton aria-label="view" onClick={() => handleAddWorkOrder(dataItem?.dataItem)}>
            <Tooltip title={<Typography variant="body1">{'Add Work Order'}</Typography>}>
              <AddCircleOutlineOutlinedIcon style={{ color: '$primary-color' }} />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
        {!isAddWorkorderPermission && (
          <Tooltip title={<Typography variant="body1">{'you do not have add work order permission'}</Typography>}>
            <IconButton aria-label="view">
              <AddCircleOutlineOutlinedIcon style={{ color: 'gray' }} />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Unit', 'Unit'));

  const { isLoading, isFetching, data: Units, refetch } = useLoadData<ApiResponse<Unit[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredUnitList = Units?.data?.filter((Unit) =>
      Object.values(Unit).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredUnits(filteredUnitList || []);
  }, [debouncedSearchTerm, Units?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Unit',
      pageName: 'Unit',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Unit',
      pageName: 'Unit',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage
      title="Lots/Units"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreateLot}
          >
            Add Lot/Unit
          </Button>
        </GuardedComponent>
      }
    >
      <div className="innerContent">
        <ReactHookForm formName="SEVE_FORM" onSubmit={() => { }} formContextProps={form}>
          <div className="mBm d-flex">
            <SelectChainItem config={selectConfig} value={selectedFilters} onSelect={handleFilterSelected} />

            <FormSelect label="Status" name="status" options={options} onChange={handleStatusChange} />
          </div>
        </ReactHookForm>
        <Paper elevation={0} style={styling.paperStyle}>
          <SiTrackGrid
            fileTitle='unit'
            columns={columns}
            dataItemKey="unitID"
            // queryConfig={getAPIKeyStore()}
            viewRoute={(value) => `profile/${value.unitID}`}
            customCell={(field) =>
              UnitsCustomGridCell({
                field,
              })
            }
            CustomComponent={form.getValues('status') !== 'archived' ? addWorkOrderElement : undefined}
            onEdit={form.getValues('status') !== 'archived' ? handleEditUnit : undefined}
            actionPermissions={{
              view: UserPermissionName.VIEW_UNIT,
              edit: UserPermissionName.EDIT_UNIT,
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            isBusy={isLoading || isFetching || isLoadingGridLayout}
            // -----------------Grid Layout-------
            dataItems={filteredUnits || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
            reFixPageNumber
          // -----------------Grid Layout-------
          />
        </Paper>
      </div>
      {isFormOpen && (
        <CreateWorkorderDialog
          open={isFormOpen}
          onClose={handleDialogClose}
          selectedFilters={{
            customerID: unitData?.customerID,
            officeID: unitData?.officeID,
            projectID: unitData?.projectID,
            unitID: unitData?.unitID,
          }}
        />
      )}
    </SiTrackPage>
  );
};
